import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeadset, faTimes, faPaperPlane, faUser, faArrowLeft, faCheck, faImage, faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import './FloatingSupport.css';

const FloatingSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [guestInfo, setGuestInfo] = useState(null); // { name, phone }
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [phoneInput, setPhoneInput] = useState(''); // Formatlanmış telefon numarası (görüntüleme için)
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null);

  // Telefon numarasını normalize et (5XXXXXXXXX formatına)
  const normalizePhoneNumber = (phone) => {
    if (!phone) return null;
    const cleanNumber = phone.replace(/\D/g, '');
    
    if (cleanNumber.length === 11 && cleanNumber.startsWith('0')) {
      return cleanNumber.substring(1); // 0'ı kaldır
    } else if (cleanNumber.length === 10 && cleanNumber.startsWith('5')) {
      return cleanNumber; // Zaten doğru formatta
    } else if (cleanNumber.length === 13 && cleanNumber.startsWith('90')) {
      return cleanNumber.substring(2); // 90'ı kaldır
    }
    
    return null;
  };

  // Telefon numarasını formatla (xxx xx xx formatında - görüntüleme için)
  const formatPhoneInput = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // 10 karakter sınırı
    const limitedNumbers = numbers.slice(0, 10);
    
    // 5 ile başlamalı kontrolü (sadece ilk karakter için)
    if (limitedNumbers.length > 0 && limitedNumbers[0] !== '5') {
      return ''; // 5 ile başlamıyorsa temizle
    }
    
    // Format: 5xx xxx xx xx (3-3-2-2)
    if (limitedNumbers.length <= 3) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 6) {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3)}`;
    } else if (limitedNumbers.length <= 8) {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6)}`;
    } else {
      return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6, 8)} ${limitedNumbers.slice(8, 10)}`;
    }
  };

  // Telefon numarası input değişikliği
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhoneInput(formatted);
  };

  // Telefon numarasından deterministik UUID oluştur (UUID v5 benzeri)
  // Bu, aynı telefon numarası için her zaman aynı UUID'yi üretir
  const phoneToUUID = (phone) => {
    if (!phone) {
      console.error('phoneToUUID: phone is null or undefined');
      return null;
    }
    
    // Telefon numarasını normalize et
    const normalized = normalizePhoneNumber(phone);
    if (!normalized) {
      console.error('phoneToUUID: failed to normalize phone', phone);
      return null;
    }
    
    // Namespace UUID (sabit bir UUID, misafir kullanıcılar için)
    const namespace = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
    
    // Daha iyi hash için telefon numarasını ve namespace'i birleştir
    const input = `guest_${normalized}_${namespace}`;
    
    // Multiple hash passes for better distribution
    let hash1 = 0;
    let hash2 = 0;
    
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash1 = ((hash1 << 5) - hash1) + char;
      hash1 = hash1 & hash1; // 32-bit integer
      hash2 = ((hash2 << 7) - hash2) + char;
      hash2 = hash2 & hash2; // 32-bit integer
    }
    
    // Combine hashes
    const combinedHash = Math.abs(hash1) * 1000000 + Math.abs(hash2);
    
    // Convert to hex and ensure 32 characters
    let hex = combinedHash.toString(16);
    
    // Pad or truncate to exactly 32 characters
    if (hex.length < 32) {
      // Pad with repeating pattern
      const padPattern = normalized.split('').map(c => parseInt(c) || 0).join('');
      while (hex.length < 32) {
        hex += padPattern;
      }
      hex = hex.substring(0, 32);
    } else if (hex.length > 32) {
      hex = hex.substring(0, 32);
    }
    
    // UUID v5 formatına dönüştür
    // Format: xxxxxxxx-xxxx-5xxx-yxxx-xxxxxxxxxxxx
    const uuid = [
      hex.substring(0, 8),
      hex.substring(8, 12),
      '5' + hex.substring(13, 16), // Version 5
      ((parseInt(hex[16] || '8', 16) & 0x3) | 0x8).toString(16) + hex.substring(17, 20), // Variant (10xx)
      hex.substring(20, 32)
    ].join('-');
    
    console.log('phoneToUUID generated:', uuid, 'for phone:', normalized);
    return uuid;
  };

  // localStorage'dan misafir bilgilerini yükle
  useEffect(() => {
    const savedGuestInfo = localStorage.getItem('chat_guest_info');
    if (savedGuestInfo) {
      const parsed = JSON.parse(savedGuestInfo);
      setGuestInfo(parsed);
      setShowGuestForm(false);
      
      // Telefon numarasını formatla (görüntüleme için)
      if (parsed.phone) {
        const formatted = formatPhoneInput(parsed.phone);
        setPhoneInput(formatted);
      }
      
      // Mesajları da yükle
      const savedMessages = localStorage.getItem(`chat_messages_${parsed.phone}`);
      if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
      }
    }
  }, []);

  // Mesajları localStorage'a kaydet
  useEffect(() => {
    if (guestInfo && messages.length > 0) {
      localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(messages));
    }
  }, [messages, guestInfo]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    let isSubscribed = true;

    const initializeChat = async () => {
      const currentUser = user || (guestInfo ? { id: phoneToUUID(guestInfo.phone), is_admin: false, first_name: guestInfo.name, last_name: '', phone: guestInfo.phone } : null);
      
      if ((user || guestInfo) && isOpen && !showGuestForm) {
        try {
          // İlk yükleme
          if (currentUser?.is_admin) {
            const { data: conversations } = await supabase
              .from('messages')
              .select(`
                *,
                sender:users!messages_sender_id_fkey(
                  id,
                  first_name,
                  last_name,
                  email,
                  is_admin
                )
              `);

            if (conversations && conversations.length > 0) {
              // En son mesajı olan ve okunmamış mesajı olan konuşmayı bul
              const lastUnreadConversation = conversations.find(conv => {
                const messages = conv.message || [];
                return messages.some(msg => 
                  msg.status === 'unread' && 
                  msg.sender_id !== user.id && 
                  !msg.sender?.is_admin
                );
              });

              if (lastUnreadConversation) {
                // Okunmamış mesajı olan konuşmayı seç ve mesajları güncelle
                await handleConversationSelect(lastUnreadConversation.conversation_id);
              }
            }
            await fetchConversations();
          } else {
            const conversationId = user ? user.id : phoneToUUID(guestInfo.phone);
            await fetchMessages(conversationId);
          }

          // Önceki subscription'ı temizle
          if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
          }

          // Yeni subscription oluştur
          const channel = supabase.channel('messages-' + Date.now());
          
          const eventHandler = async (payload) => {
            if (!isSubscribed) return;
            
            try {
              // Payload kontrolü
              if (!payload.new?.message) return;

              // Son mesajı kontrol et
              const lastMessage = payload.new.message[payload.new.message.length - 1];
              if (!lastMessage) return;

              const currentUserId = user ? user.id : phoneToUUID(guestInfo?.phone);
              
              // Eğer mesaj kendimizden geliyorsa ve zaten state güncel ise işleme
              // sender_id null olabilir (misafir kullanıcılar için), bu durumda sender.id kullan
              const lastMessageSenderId = lastMessage.sender_id || lastMessage.sender?.id;
              if (lastMessageSenderId === currentUserId && 
                  messages.some(m => m.id === lastMessage.id)) {
                return;
              }

              if (currentUser?.is_admin) {
                // Admin için: Konuşmalar listesini güncelle
                if (selectedConversation === payload.new.conversation_id) {
                  // Eğer admin şu anda bu konuşmayı görüntülüyorsa ve mesaj kullanıcıdan geldiyse
                  const hasUnreadUserMessages = payload.new.message.some(msg => 
                    msg.status === 'unread' && msg.sender_id !== user.id
                  );

                  if (hasUnreadUserMessages) {
                    const updatedMessages = payload.new.message.map(msg => ({
                      ...msg,
                      status: msg.sender_id !== user.id ? 'read' : msg.status
                    }));

                    // Mesajları bir kez güncelle
                    await supabase
                      .from('messages')
                      .update({
                        message: updatedMessages
                      })
                      .eq('conversation_id', selectedConversation);

                    setMessages(updatedMessages);
                  } else {
                    setMessages(payload.new.message);
                  }
                }
                // Konuşmalar listesini güncelle
                await fetchConversations();
              } else {
                // Normal kullanıcı veya misafir için: Kendi konuşmasını güncelle
                const conversationId = user ? user.id : phoneToUUID(guestInfo?.phone);
                if (payload.new.conversation_id === conversationId) {
                  setMessages(payload.new.message);
                  
                  // localStorage'a kaydet
                  if (guestInfo) {
                    localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(payload.new.message));
                  }
                  
                  // Mesajları okundu olarak işaretle
                  if (isOpen && payload.new.message.some(msg => {
                    const msgSenderId = msg.sender_id || msg.sender?.id;
                    return msg.status === 'unread' && 
                           msgSenderId !== currentUserId && 
                           msg.sender?.is_admin;
                  })) {
                    const updatedMessages = payload.new.message.map(msg => {
                      const msgSenderId = msg.sender_id || msg.sender?.id;
                      return {
                        ...msg,
                        // Sadece admin mesajlarını read olarak işaretle
                        status: (msgSenderId !== currentUserId && msg.sender?.is_admin) ? 'read' : msg.status
                      };
                    });

                    await supabase
                      .from('messages')
                      .update({
                        message: updatedMessages
                      })
                      .eq('conversation_id', conversationId);
                  }
                }
              }

              scrollToBottom();
            } catch (error) {
              console.error('Error in subscription handler:', error);
            }
          };
          
          subscriptionRef.current = channel
            .on(
              'postgres_changes',
              {
                event: 'INSERT',
                schema: 'public',
                table: 'messages'
              },
              eventHandler
            )
            .on(
              'postgres_changes',
              {
                event: 'UPDATE',
                schema: 'public',
                table: 'messages',
                  filter: currentUser?.is_admin 
                    ? selectedConversation 
                      ? `conversation_id=eq.${selectedConversation}`
                      : undefined 
                    : `conversation_id=eq.${user ? user.id : phoneToUUID(guestInfo?.phone)}`
              },
              eventHandler
            )
            .subscribe();
        } catch (error) {
          console.error('Error initializing chat:', error);
        }
      }
    };

    initializeChat();

    // Cleanup function
    return () => {
      isSubscribed = false;
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, guestInfo?.phone, isOpen, selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Okunmamış mesaj sayısını güncelle
    const currentUser = user || (guestInfo ? { id: phoneToUUID(guestInfo.phone), is_admin: false } : null);
    
    if (currentUser) {
      if (currentUser.is_admin) {
        // Admin için tüm konuşmalardaki okunmamış mesajları topla
        const totalUnread = conversations.reduce((total, conv) => total + conv.unread, 0);
        setUnreadCount(totalUnread);
      } else {
        // Normal kullanıcı veya misafir için kendi mesajlarındaki okunmamışları say
        const currentUserId = user ? user.id : phoneToUUID(guestInfo.phone);
        const unreadMessages = messages.filter(msg => {
          const msgSenderId = msg.sender_id || msg.sender?.id;
          return msg.status === 'unread' && 
                 msgSenderId !== currentUserId && 
                 msg.sender?.is_admin;
        }).length;
        setUnreadCount(unreadMessages);
      }
    }
  }, [messages, conversations, user, guestInfo]);

  // Yeni useEffect - Sayfa yüklendiğinde mesajları kontrol et
  useEffect(() => {
    const currentUser = user || (guestInfo ? { id: phoneToUUID(guestInfo.phone), is_admin: false } : null);
    
    const checkUnreadMessages = async () => {
      if (!currentUser) return;

      try {
        if (currentUser.is_admin) {
          // Admin için tüm konuşmaları kontrol et
          await fetchConversations();
        } else {
          // Normal kullanıcı veya misafir için kendi mesajlarını kontrol et
          const conversationId = user ? user.id : phoneToUUID(guestInfo?.phone);
          if (!conversationId) return;
          
          const { data, error } = await supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conversationId)
            .single();

          if (error && error.code !== 'PGRST116') {
            console.error('Error checking messages:', error);
            return;
          }

          if (data?.message) {
            const currentUserId = user ? user.id : phoneToUUID(guestInfo.phone);
            const unreadMessages = data.message.filter(msg => {
              const msgSenderId = msg.sender_id || msg.sender?.id;
              return msg.status === 'unread' && 
                     msgSenderId !== currentUserId && 
                     msg.sender?.is_admin;
            }).length;
            setUnreadCount(unreadMessages);
          }
        }
      } catch (error) {
        console.error('Error in checkUnreadMessages:', error);
      }
    };

    checkUnreadMessages();

    // Realtime subscription for unread messages
    const channel = supabase.channel('unread-messages');
    
    const subscription = channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        filter: currentUser?.is_admin 
          ? undefined 
          : `conversation_id=eq.${user ? user.id : phoneToUUID(guestInfo?.phone)}`
        },
        () => {
          checkUnreadMessages();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, guestInfo]);

  const fetchConversations = async () => {
    try {
      const { data: conversations, error } = await supabase
        .from('messages')
        .select('*');

      if (error) {
        console.error('Error fetching conversations:', error);
        return;
      }

      // Konuşmaları telefon numarasına göre grupla (eşsiz konuşmalar)
      const conversationMap = new Map();
      
      conversations.forEach(conv => {
        const conversationId = conv.conversation_id;
        const messages = conv.message || [];
        const lastMessage = messages[messages.length - 1];
        
        // Misafir kullanıcı bilgilerini al
        const guestName = conv.guest_name;
        const guestPhone = conv.guest_phone;
        
        // İlk mesajdan kullanıcı bilgilerini al
        const firstUserMessage = messages.find(m => !m.sender?.is_admin);
        const userName = guestName || firstUserMessage?.sender?.first_name || 'Misafir';
        const userLastName = firstUserMessage?.sender?.last_name || '';
        const userPhone = guestPhone || firstUserMessage?.sender?.phone || conversationId;
        
        // Sadece okunmamış ve admin olmayan kullanıcılardan gelen mesajları say
        const unreadCount = messages.filter(m => 
          m.status === 'unread' && 
          !m.sender?.is_admin
        ).length;

        // Eğer bu conversation_id zaten varsa, daha yeni mesajı olanı kullan
        if (!conversationMap.has(conversationId) || 
            (lastMessage && conversationMap.get(conversationId).lastMessage?.created_at < lastMessage.created_at)) {
          conversationMap.set(conversationId, {
            id: conversationId,
            lastMessage: lastMessage,
            user: {
              first_name: userName,
              last_name: userLastName,
              phone: userPhone,
              is_admin: false
            },
            unread: unreadCount,
            is_complete: conv.is_complete
          });
        }
      });

      // Map'i array'e çevir ve son mesaj zamanına göre sırala
      const processedConversations = Array.from(conversationMap.values())
        .sort((a, b) => {
          const timeA = a.lastMessage?.created_at || '';
          const timeB = b.lastMessage?.created_at || '';
          return timeB.localeCompare(timeA);
        });

      setConversations(processedConversations);
    } catch (error) {
      console.error('Error in fetchConversations:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching messages:', error);
        // localStorage'dan yükle
        if (guestInfo) {
          const savedMessages = localStorage.getItem(`chat_messages_${guestInfo.phone}`);
          if (savedMessages) {
            setMessages(JSON.parse(savedMessages));
          }
        }
        return;
      }

      let updatedMessages;
      const currentUserId = user ? user.id : phoneToUUID(guestInfo?.phone);
      
      if (!user?.is_admin) {
        // Son kullanıcı veya misafir için: Admin mesajlarını read olarak işaretle
        updatedMessages = (data?.message || []).map(msg => {
          const msgSenderId = msg.sender_id || msg.sender?.id;
          return {
            ...msg,
            status: (msgSenderId !== currentUserId && msg.sender?.is_admin) ? 'read' : msg.status
          };
        });
      } else {
        // Admin için: Tüm kullanıcı mesajlarını read olarak işaretle
        const hasUnreadMessages = (data?.message || []).some(msg => {
          const msgSenderId = msg.sender_id || msg.sender?.id;
          return msg.status === 'unread' && 
                 msgSenderId !== currentUserId && 
                 !msg.sender?.is_admin;
        });

        if (hasUnreadMessages) {
          updatedMessages = (data?.message || []).map(msg => {
            const msgSenderId = msg.sender_id || msg.sender?.id;
            return {
              ...msg,
              status: msgSenderId !== currentUserId ? 'read' : msg.status
            };
          });

          // Mesajları güncelle
          const { error: updateError } = await supabase
            .from('messages')
            .update({ 
              message: updatedMessages
            })
            .eq('conversation_id', conversationId);

          if (updateError) {
            console.error('Error updating message read status:', updateError);
          } else {
            // Konuşma listesini güncelle
            await fetchConversations();
          }
        } else {
          updatedMessages = data?.message || [];
        }
      }

      setMessages(updatedMessages);
      
      // localStorage'a kaydet (misafir için)
      if (guestInfo && updatedMessages.length > 0) {
        localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(updatedMessages));
      }
    } catch (error) {
      console.error('Error in fetchMessages:', error);
    }
  };

  const handleClick = () => {
    const isMobile = window.innerWidth <= 768;
    
    if (!isOpen) {
      // Eğer kullanıcı giriş yapmamışsa ve misafir bilgileri de yoksa formu göster
      // Admin kullanıcılar için form gösterilmez
      if (!user && !guestInfo) {
        setShowGuestForm(true);
      } else if (user?.is_admin) {
        // Admin kullanıcılar için formu kapat
        setShowGuestForm(false);
      }
      
      document.body.classList.add('chat-open');
      
      // Sadece mobil cihazlarda scroll position'ı kaydet ve body'yi fixed yap
      if (isMobile) {
        setScrollPosition(window.pageYOffset);
        document.body.style.top = `-${window.pageYOffset}px`;
      }
    } else {
      document.body.classList.remove('chat-open');
      
      // Sadece mobil cihazlarda scroll position'ı geri yükle
      if (isMobile) {
        document.body.style.top = '';
        window.scrollTo(0, scrollPosition);
      }
    }
    setIsOpen(!isOpen);
  };

  // Chat penceresi kapandığında body class'ını temizle
  useEffect(() => {
    return () => {
      document.body.classList.remove('chat-open');
      
      // Sadece mobil cihazlarda body style'ı temizle
      if (window.innerWidth <= 768) {
        document.body.style.top = '';
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    // Misafir kullanıcı için bilgileri kontrol et (admin kullanıcılar hariç)
    if (!user?.is_admin && !user && !guestInfo) {
      setShowGuestForm(true);
      return;
    }

    // Mesajı sakla ve input'u hemen temizle (daha iyi UX)
    let messageToSend = newMessage.trim();
    setNewMessage('');

    try {
      const currentUser = user || (guestInfo ? { 
        id: phoneToUUID(guestInfo.phone), 
        is_admin: false, 
        first_name: guestInfo.name, 
        last_name: '',
        phone: guestInfo.phone 
      } : null);
      
      if (!currentUser) return;
      
      // Misafir kullanıcılar için telefon numarasından UUID oluştur
      let conversationId = currentUser.is_admin ? selectedConversation : (user ? user.id : phoneToUUID(guestInfo?.phone));
      
      // Eğer conversationId null ise hata ver
      if (!conversationId && !currentUser.is_admin) {
        console.error('Failed to generate conversation ID for guest user');
        setNewMessage(messageToSend);
        alert('Telefon numarası geçersiz. Lütfen tekrar deneyin.');
        return;
      }
      
      const { data: existingConversation, error: fetchError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching conversation:', fetchError);
        // Hata durumunda mesajı geri yükle
        setNewMessage(messageToSend);
        return;
      }

      const newMessageObj = {
        id: Date.now(),
        // Misafir kullanıcılar için sender_id null (users tablosunda olmadıkları için)
        sender_id: user ? user.id : null,
        sender: {
          id: currentUser.id,
          first_name: currentUser.first_name,
          last_name: currentUser.last_name || '',
          is_admin: currentUser.is_admin || false,
          phone: guestInfo?.phone || user?.phone || null
        },
        message: messageToSend,
        created_at: new Date().toISOString(),
        status: 'unread'
      };

      // Eğer konuşma tamamlandı olarak işaretlenmişse ve kullanıcı mesaj gönderiyorsa
      const shouldReactivate = existingConversation?.is_complete && !currentUser.is_admin;

      if (!existingConversation) {
        const { data: newConversation, error: insertError } = await supabase
          .from('messages')
          .insert([{
            conversation_id: conversationId,
            sender_id: user ? user.id : null, // Misafir kullanıcılar için null (users tablosunda olmadıkları için)
            message: [newMessageObj],
            is_complete: false,
            guest_name: guestInfo?.name || null,
            guest_phone: guestInfo?.phone || null
          }])
          .select()
          .single();

        if (insertError) {
          console.error('Error creating new conversation:', insertError);
          // Hata durumunda mesajı geri yükle
          setNewMessage(messageToSend);
          // Hata olsa bile localStorage'a kaydet
          if (guestInfo) {
            const savedMessages = localStorage.getItem(`chat_messages_${guestInfo.phone}`) || '[]';
            const messages = JSON.parse(savedMessages);
            messages.push(newMessageObj);
            localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(messages));
            setMessages(messages);
          }
          return;
        }

        setMessages([newMessageObj]);
        
        // localStorage'a kaydet
        if (guestInfo) {
          localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify([newMessageObj]));
        }

        // Yeni konuşma başlatıldığında Telegram bildirimi gönder
        if (!currentUser.is_admin) {
          try {
            const headers = {
              'Content-Type': 'application/json'
            };
            
            // Sadece normal kullanıcılar için token gönder
            const token = localStorage.getItem('token');
            if (token && user) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/telegram/chat-notification`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({
                user: user ? {
                  first_name: user.first_name,
                  last_name: user.last_name,
                  email: user.email,
                  phone: user.phone
                } : {
                  first_name: guestInfo.name,
                  last_name: '',
                  email: null,
                  phone: guestInfo.phone
                },
                type: 'new',
                is_guest: !user // Misafir kullanıcı olduğunu belirt
              })
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('Failed to send Telegram notification:', errorData);
            }
          } catch (error) {
            console.error('Error sending Telegram notification:', error);
          }
        }
      } else {
        const updatedMessages = [...(existingConversation.message || []), newMessageObj];
        
        const { error: updateError } = await supabase
          .from('messages')
          .update({
            message: updatedMessages,
            is_complete: shouldReactivate ? false : existingConversation.is_complete
          })
          .eq('conversation_id', conversationId);

        if (updateError) {
          console.error('Error updating conversation:', updateError);
          // Hata durumunda mesajı geri yükle
          setNewMessage(messageToSend);
          return;
        }

        setMessages(updatedMessages);
        
        // localStorage'a kaydet
        if (guestInfo) {
          localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(updatedMessages));
        }
        
        // Konuşma yeniden aktif hale getirildiyse konuşmalar listesini güncelle
        if (shouldReactivate) {
          await fetchConversations();

          // Tamamlanmış konuşma yeniden açıldığında Telegram bildirimi gönder
          try {
            const headers = {
              'Content-Type': 'application/json'
            };
            
            // Sadece normal kullanıcılar için token gönder
            const token = localStorage.getItem('token');
            if (token && user) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/telegram/chat-notification`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({
                user: user ? {
                  first_name: user.first_name,
                  last_name: user.last_name,
                  email: user.email,
                  phone: user.phone
                } : {
                  first_name: guestInfo.name,
                  last_name: '',
                  email: null,
                  phone: guestInfo.phone
                },
                type: 'reactivated',
                is_guest: !user // Misafir kullanıcı olduğunu belirt
              })
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('Failed to send Telegram notification:', errorData);
            }
          } catch (error) {
            console.error('Error sending Telegram notification:', error);
          }
        }
      }

      scrollToBottom();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Hata durumunda mesajı geri yükle
      setNewMessage(messageToSend);
    }
  };

  const handleMarkAsComplete = async (isComplete) => {
    if (!selectedConversation) return;

    try {
      const { error } = await supabase
        .from('messages')
        .update({ is_complete: isComplete })
        .eq('conversation_id', selectedConversation);

      if (error) {
        console.error('Error updating conversation status:', error);
        return;
      }

      // Konuşma listesini güncelle
      await fetchConversations();
    } catch (error) {
      console.error('Error in handleMarkAsComplete:', error);
    }
  };

  const markMessagesAsRead = async (messages) => {
    if (!user) return messages;

    return messages.map(msg => ({
      ...msg,
      status: msg.sender_id === user.id ? msg.status : 'read'
    }));
  };

  const handleConversationSelect = async (conversationId) => {
    setSelectedConversation(conversationId);
    await fetchMessages(conversationId);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Misafir kullanıcı için bilgileri kontrol et (admin kullanıcılar hariç)
    if (!user?.is_admin && !user && !guestInfo) {
      setShowGuestForm(true);
      return;
    }
    
    const currentUser = user || (guestInfo ? { 
        id: phoneToUUID(guestInfo.phone), 
        is_admin: false, 
        first_name: guestInfo.name, 
        last_name: '',
        phone: guestInfo.phone 
      } : null);
    
    if (!currentUser) return;

    try {
      setIsUploading(true);

      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır.');
        return;
      }

      // Sadece resim dosyalarını kabul et
      if (!file.type.startsWith('image/')) {
        alert('Sadece resim dosyaları yükleyebilirsiniz.');
        return;
      }

      // Misafir kullanıcılar için telefon numarasından UUID oluştur
      const conversationId = currentUser.is_admin ? selectedConversation : (user ? user.id : phoneToUUID(guestInfo.phone));
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `chat-images/${conversationId}/${fileName}`;

      // Supabase Storage'a yükle
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('chat-media')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        alert('Resim yüklenirken bir hata oluştu.');
        return;
      }

      // Public URL al
      const { data: { publicUrl } } = supabase.storage
        .from('chat-media')
        .getPublicUrl(filePath);

      // Mesaj olarak gönder
      const newMessageObj = {
        id: Date.now(),
        sender_id: user ? user.id : null, // Misafir kullanıcılar için null
        sender: {
          id: currentUser.id,
          first_name: currentUser.first_name,
          last_name: currentUser.last_name || '',
          is_admin: currentUser.is_admin || false,
          phone: guestInfo?.phone || user?.phone || null
        },
        message: '📷 Fotoğraf',
        image_url: publicUrl,
        created_at: new Date().toISOString(),
        status: 'unread'
      };

      const { data: existingConversation, error: fetchError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching conversation:', fetchError);
        return;
      }

      if (!existingConversation) {
        await supabase
          .from('messages')
          .insert([{
            conversation_id: conversationId,
            sender_id: user ? user.id : null, // Misafir kullanıcılar için null
            message: [newMessageObj],
            is_complete: false,
            guest_name: guestInfo?.name || null,
            guest_phone: guestInfo?.phone || null
          }]);
      } else {
        const updatedMessages = [...(existingConversation.message || []), newMessageObj];
        
        await supabase
          .from('messages')
          .update({
            message: updatedMessages
          })
          .eq('conversation_id', conversationId);
      }
      
      // localStorage'a kaydet
      if (guestInfo) {
        const savedMessages = localStorage.getItem(`chat_messages_${guestInfo.phone}`) || '[]';
        const messages = JSON.parse(savedMessages);
        messages.push(newMessageObj);
        localStorage.setItem(`chat_messages_${guestInfo.phone}`, JSON.stringify(messages));
        setMessages(messages);
      } else {
        setMessages(prev => [...prev, newMessageObj]);
      }

      scrollToBottom();
    } catch (error) {
      console.error('Error in handleImageUpload:', error);
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsUploading(false);
    }
  };

  // Misafir bilgileri formunu işle
  const handleGuestSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name')?.trim();
    const phone = phoneInput; // Formatlanmış telefon numarası
    
    if (!name || !phone) {
      alert('Lütfen isim ve telefon numarası girin.');
      return;
    }
    
    // Telefon numarasını normalize et (boşluksuz, API'ye gönderilecek format)
    const normalizedPhone = normalizePhoneNumber(phone);
    if (!normalizedPhone || normalizedPhone.length !== 10) {
      alert('Geçerli bir telefon numarası girin (5xx xxx xx xx formatında, 10 haneli)');
      return;
    }
    
    // Format kontrolü: 5 ile başlamalı
    if (!normalizedPhone.startsWith('5')) {
      alert('Telefon numarası 5 ile başlamalıdır (5xx xxx xx xx)');
      return;
    }
    
    const guestData = { name, phone: normalizedPhone };
    setGuestInfo(guestData);
    setShowGuestForm(false);
    setPhoneInput(''); // Input'u temizle
    
    // localStorage'a kaydet
    localStorage.setItem('chat_guest_info', JSON.stringify(guestData));
    
    // Mevcut mesajları yükle
    const savedMessages = localStorage.getItem(`chat_messages_${normalizedPhone}`);
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
  };

  // Mesaj render fonksiyonunu güncelle
  const renderMessage = (message) => {
    const senderIsAdmin = message.sender?.is_admin || false;
    const currentUserId = user ? user.id : phoneToUUID(guestInfo?.phone);
    // Misafir kullanıcılar için sender_id null olabilir, bu durumda sender objesindeki id'yi kullan
    const messageSenderId = message.sender_id || message.sender?.id;
    const messageClass = `message ${senderIsAdmin ? 'admin' : 'user'} ${messageSenderId === currentUserId ? 'sent' : 'received'}`;
    
    return (
      <div key={message.id} className={messageClass}>
        <div className="message-content">
          {senderIsAdmin && (message.sender_id || message.sender?.id) !== currentUserId && (
            <div className="admin-badge">
              <FontAwesomeIcon icon={faHeadset} className="me-1" />
              Destek
            </div>
          )}
          {message.image_url ? (
            <div className="message-image">
              <img 
                src={message.image_url} 
                alt="Gönderilen fotoğraf" 
                onClick={() => window.open(message.image_url, '_blank')}
              />
            </div>
          ) : (
            <div className="message-text">{message.message}</div>
          )}
          <span className="message-time">
            {formatTime(message.created_at)}
          </span>
        </div>
      </div>
    );
  };

  const handleGuestFormCancel = () => {
    setShowGuestForm(false);
    setPhoneInput(''); // Input'u temizle
    if (!guestInfo) {
      setIsOpen(false);
    }
  };

  // Mobil cihazlarda input alanına focus olduğunda zoom'u engellemek için
  useEffect(() => {
    const preventZoom = () => {
      const metaViewport = document.querySelector('meta[name=viewport]');
      if (metaViewport) {
        metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
    };

    const handleInputFocus = () => {
      preventZoom();
    };

    const handleInputBlur = () => {
      // Input'tan çıkıldığında özgün viewport ayarlarını geri yükleyebilirsiniz, 
      // ancak zoom problemi çözmek istediğimiz için bu örnekte yapmıyoruz
    };

    if (chatInputRef.current) {
      chatInputRef.current.addEventListener('focus', handleInputFocus);
      chatInputRef.current.addEventListener('blur', handleInputBlur);
    }

    return () => {
      if (chatInputRef.current) {
        chatInputRef.current.removeEventListener('focus', handleInputFocus);
        chatInputRef.current.removeEventListener('blur', handleInputBlur);
      }
    };
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-title">
              {user?.is_admin && selectedConversation ? (
                <>
                  <button 
                    className="back-button" 
                    onClick={() => {
                      setSelectedConversation(null);
                      setMessages([]);
                      fetchConversations();
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  <span>Konuşma Detayı</span>
                  <div className="conversation-status">
                    {conversations.find(c => c.id === selectedConversation)?.is_complete ? (
                      <button 
                        className="status-badge completed"
                        onClick={() => handleMarkAsComplete(false)}
                        title="Konuşmayı tekrar aktif hale getir"
                      >
                        <FontAwesomeIcon icon={faArrowRotateLeft} />
                      </button>
                    ) : (
                      <button 
                        className="complete-button"
                        onClick={() => handleMarkAsComplete(true)}
                        title="Konuşmayı tamamlandı olarak işaretle"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faHeadset} className="me-2" />
                  Müşteri Hizmetleri
                </>
              )}
            </div>
            <button className="close-button" onClick={() => {
              document.body.classList.remove('chat-open');
              
              // Sadece mobil cihazlarda scroll position'ı geri yükle
              if (window.innerWidth <= 768) {
                document.body.style.top = '';
                window.scrollTo(0, scrollPosition);
              }
              
              setIsOpen(false);
            }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          {(user?.is_admin ? false : (showGuestForm || (!user && !guestInfo))) ? (
            <div className="login-prompt">
              <FontAwesomeIcon icon={faUser} className="login-icon" />
              <h3>Bilgilerinizi Girin</h3>
              <p>Canlı destek hizmetimizden yararlanmak için lütfen isim ve telefon numaranızı girin.</p>
              <form onSubmit={handleGuestSubmit} className="guest-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Adınız Soyadınız"
                  required
                  className="guest-input"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="5xx xxx xx xx"
                  value={phoneInput}
                  onChange={handlePhoneChange}
                  maxLength={13} // 5xx xxx xx xx = 13 karakter (boşluklarla)
                  required
                  className="guest-input"
                  pattern="^5[0-9]{2} [0-9]{3} [0-9]{2} [0-9]{2}$"
                />
                <div className="guest-form-buttons">
                  <button 
                    type="submit"
                    className="guest-submit-button"
                  >
                    Devam Et
                  </button>
                  <button 
                    type="button"
                    onClick={handleGuestFormCancel}
                    className="guest-cancel-button"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          ) : (
            (user?.is_admin || guestInfo) && !selectedConversation && user?.is_admin ? (
              <div className="conversations-list">
                {conversations.length === 0 ? (
                  <div className="no-conversations">
                    <FontAwesomeIcon icon={faHeadset} className="me-2" style={{ color: '#8B4513', fontSize: '2rem', marginBottom: '10px' }} />
                    <div>Henüz hiç destek talebi yok.</div>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="conversation-item"
                      onClick={() => handleConversationSelect(conv.id)}
                    >
                      <div className="conversation-info">
                        <FontAwesomeIcon icon={faUser} className="me-2" style={{ color: '#8B4513' }}/>
                        <div>
                          <div className="user-name">
                            {conv.user?.first_name} {conv.user?.last_name || ''}
                            {conv.user?.phone && (
                              <span style={{ fontSize: '0.8rem', color: '#6c757d', marginLeft: '8px' }}>
                                ({conv.user.phone})
                              </span>
                            )}
                          </div>
                          <div className="last-message">
                            {conv.lastMessage?.message || 'Mesaj yok'}
                          </div>
                        </div>
                      </div>
                      {conv.unread > 0 && (
                        <div className="unread-badge">{conv.unread}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ) : (
              <>
                <div className="chat-messages">
                  {messages.map((message) => {
                    return renderMessage(message);
                  })}
                  <div ref={messagesEndRef} />
                </div>
                {((user?.is_admin && selectedConversation) || (!user?.is_admin)) && (
                  <form onSubmit={handleSubmit} className="chat-input-container">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Mesajınızı yazın..."
                      className="chat-input"
                      ref={chatInputRef}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(file);
                          e.target.value = ''; // Input'u temizle
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="upload-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </button>
                    <button type="submit" className="send-button" disabled={isUploading}>
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                  </form>
                )}
              </>
            )
          )}
        </div>
      )}
      
      <div 
        className={`floating-support ${isOpen ? 'active' : ''}`}
        onClick={handleClick}
      >
        <div className="support-icon">
          <FontAwesomeIcon 
            icon={faHeadset}
            className="icon"
          />
        </div>
        {unreadCount > 0 && (
          <div className="unread-notification">
            {unreadCount}
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingSupport; 